/**
 * Shared client-side pagination for data-heavy admin tables.
 *
 * `renderPager` produces the numbered control (reusing the `.pager` styles);
 * `bindPager` wires the buttons to a page-change callback. State (current page)
 * is owned by the caller so the same helper works for any table.
 */

/** Build the windowed page list, e.g. [1, '…', 7, 8, 9, '…', 42]. */
export function pageWindow(current: number, pageCount: number): Array<number | '…'> {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const out: Array<number | '…'> = [1];
  const lo = Math.max(2, current - 1);
  const hi = Math.min(pageCount - 1, current + 1);
  if (lo > 2) out.push('…');
  for (let p = lo; p <= hi; p++) out.push(p);
  if (hi < pageCount - 1) out.push('…');
  out.push(pageCount);
  return out;
}

/** Selectable rows-per-page sizes offered in the pager. */
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

/**
 * Render the pager footer for `total` rows at `pageSize`: a rows-per-page
 * selector, the numbered page buttons (only when more than one page), and a
 * range readout. Always renders when `total > 0` so the size selector stays
 * available even on a single page. `label` names the unit (e.g. "players").
 */
export function renderPager(
  page: number,
  total: number,
  pageSize: number,
  label = 'rows',
  capped = false,
): string {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const totalLabel = `${total.toLocaleString()}${capped ? '+' : ''}`;
  const btn = (
    text: string,
    target: number,
    opts: { active?: boolean; disabled?: boolean } = {},
  ): string =>
    `<button class="pager-btn${opts.active ? ' active' : ''}" data-page="${target}"${opts.disabled ? ' disabled' : ''}>${text}</button>`;
  const sizeSelect = `<label class="pager-size">Rows
    <select class="pager-size-select">${PAGE_SIZE_OPTIONS.map(
      (n) => `<option value="${n}"${n === pageSize ? ' selected' : ''}>${n}</option>`,
    ).join('')}</select>
  </label>`;
  const pageButtons =
    pageCount > 1
      ? btn('‹ Prev', page - 1, { disabled: page <= 1 }) +
        pageWindow(page, pageCount)
          .map((p) =>
            p === '…' ? '<span class="pager-gap">…</span>' : btn(String(p), p, { active: p === page }),
          )
          .join('') +
        btn('Next ›', page + 1, { disabled: page >= pageCount })
      : '';
  return `<div class="pager">
    ${sizeSelect}
    ${pageButtons}
    <span class="muted pager-info">${from.toLocaleString()}–${to.toLocaleString()} of ${totalLabel} ${label}</span>
  </div>`;
}

/**
 * Wire the pager controls inside `scope`: page buttons → `onPage`, and (if
 * provided) the rows-per-page selector → `onSize`.
 */
export function bindPager(
  scope: HTMLElement,
  onPage: (page: number) => void,
  onSize?: (size: number) => void,
): void {
  scope.querySelectorAll<HTMLButtonElement>('.pager-btn[data-page]').forEach((btn) => {
    if (btn.disabled) return;
    btn.addEventListener('click', () => onPage(Number(btn.dataset.page)));
  });
  if (onSize) {
    const sel = scope.querySelector<HTMLSelectElement>('.pager-size-select');
    sel?.addEventListener('change', () => onSize(Number(sel.value)));
  }
}
